import CdoFieldColour from '@cdo/apps/blockly/addons/cdoFieldColour';
import CdoFieldImage from '@cdo/apps/blockly/addons/cdoFieldImage';
import {CdoFieldImageDropdown} from '@cdo/apps/blockly/addons/cdoFieldImageDropdown';
import CdoFieldLabel from '@cdo/apps/blockly/addons/cdoFieldLabel';
import CdoFieldVariable from '@cdo/apps/blockly/addons/cdoFieldVariable';
import FunctionEditor from '@cdo/apps/blockly/addons/functionEditor';
import {BlockStyles} from '@cdo/apps/blockly/constants';
import {registerCustomProcedureBlocks} from '@cdo/apps/blockly/utils';
import i18n from '@cdo/locale';

import CdoFieldDanceAi from '../ai/cdoFieldDanceAi';
import {GENERATED_DANCER_STORAGE_KEY} from '../ai/constants';
import {GENERATED_DANCER} from '../constants';
import {resolveDancerAssets} from '../lottie/LottieDancerUtils';

// This color palette is limited to colors which have different hues, therefore
// it should not contain different shades of the same color such as
// ['#ff0000', '#cc0000', '#880000'].
const limitedColours = [
  // fully-saturated primary colors
  '#ff0000', // RED
  '#00ff00', // GREEN
  '#0000ff', // BLUE

  // fully-saturated secondary colors
  '#ffff00', // YELLOW
  '#00ffff', // CYAN
  '#ff00ff', // MAGENTA

  // some "tertiary" colors
  '#ff8800', // ORANGE
  '#8800ff', // PURPLE
  '#00ff88', // LIME
];

function getGeneratedDancerHeadUrl() {
  // We avoid using the head of the default dancer as it a simple gray ellipse
  // which doesn't "read" as a dancer.
  let headUrl = '/blockly/media/skins/dance/default_dancer.png';
  if (sessionStorage.getItem(GENERATED_DANCER_STORAGE_KEY)) {
    const {urls} = resolveDancerAssets({sourceTag: 'blockly'});
    headUrl = urls.headUrl;
  }
  return headUrl;
}

const customInputTypes = {
  spritePicker: {
    addInput(blockly, block, inputConfig, currentInputRow) {
      block.getVars = function () {
        return [block.getFieldValue(inputConfig.name)];
      };

      currentInputRow
        .appendField(inputConfig.label)
        .appendField(
          new CdoFieldVariable(
            null,
            null,
            null,
            Blockly.BlockValueType.SPRITE,
            i18n.sprite()
          ),
          inputConfig.name
        );
    },
    generateCode(block, arg) {
      return `'${block.getFieldValue(arg.name)}'`;
    },
  },
  limitedColourPicker: {
    addInput(blockly, block, inputConfig, currentInputRow) {
      const options = {
        colourOptions: limitedColours,
        columns: 3,
      };
      currentInputRow
        .appendField(inputConfig.label)
        .appendField(new CdoFieldColour('#ff0000', undefined, options), 'VAL');
    },
    generateCode(block, arg) {
      return `'${block.getFieldValue(arg.name)}'`;
    },
  },
  danceAi: {
    addInput(blockly, block, inputConfig, currentInputRow) {
      const newField = new CdoFieldDanceAi();
      currentInputRow
        .appendField(inputConfig.label)
        .appendField(newField, inputConfig.name);
    },
    generateCode(block, arg) {
      return block.getFieldValue(arg.name);
    },
  },
  dancerPicker: {
    addInput(blockly, block, inputConfig, currentInputRow) {
      const options = inputConfig.options.map(option => {
        let name = option;
        // Options may be JSON-encoded strings (ex. "/"BEAR/"") or simple strings (ex. "sprites").
        // Blockly needs the full option for code generation, but we just want the name for the head image URL.
        try {
          name = JSON.parse(name);
        } catch {}
        if (name === GENERATED_DANCER) {
          return [getGeneratedDancerHeadUrl(), option];
        }
        return [`/blockly/media/skins/dance/${name.toLowerCase()}.png`, option];
      });
      currentInputRow
        .appendField(inputConfig.label)
        .appendField(
          new CdoFieldImageDropdown(options, 40, 40),
          inputConfig.name
        );
    },
    generateCode(block, arg) {
      return block.getFieldValue(arg.name);
    },
  },
  generatedDancerImage: {
    addInput(blockly, block, inputConfig, currentInputRow) {
      currentInputRow
        .appendField(inputConfig.label)
        .appendField(
          new CdoFieldImage(getGeneratedDancerHeadUrl(), 40, 40),
          inputConfig.name
        );
    },
    generateCode(block, arg) {
      return `"${GENERATED_DANCER}"`;
    },
  },
};

export default {
  customInputTypes,
  install(blockly) {
    registerCustomProcedureBlocks();
    // Legacy style block definitions :(
    const generator = blockly.getGenerator();

    const behaviorEditor = (Blockly.behaviorEditor = new FunctionEditor(
      {
        FUNCTION_HEADER: i18n.behaviorEditorHeader(),
        FUNCTION_NAME_LABEL: i18n.behaviorEditorLabel(),
        FUNCTION_DESCRIPTION_LABEL: i18n.behaviorEditorDescription(),
      },
      'behavior_definition',
      {
        [Blockly.BlockValueType.SPRITE]: 'sprite_parameter_get',
      },
      false /* disableParamEditing */,
      [
        Blockly.BlockValueType.NUMBER,
        Blockly.BlockValueType.STRING,
        Blockly.BlockValueType.COLOUR,
        Blockly.BlockValueType.BOOLEAN,
        Blockly.BlockValueType.SPRITE,
        Blockly.BlockValueType.LOCATION,
      ]
    ));

    Blockly.Blocks.sprite_variables_get = {
      // Variable getter.
      init: function () {
        var fieldLabel = new CdoFieldLabel(Blockly.Msg.VARIABLES_GET_ITEM);
        // Must be marked EDITABLE so that cloned blocks share the same var name
        fieldLabel.EDITABLE = true;
        this.setHelpUrl(Blockly.Msg.VARIABLES_GET_HELPURL);
        this.appendDummyInput()
          .appendField(Blockly.Msg.VARIABLES_GET_TITLE)
          .appendField(
            Blockly.disableVariableEditing
              ? fieldLabel
              : new CdoFieldVariable(
                  Blockly.Msg.VARIABLES_SET_ITEM,
                  null,
                  null,
                  Blockly.BlockValueType.SPRITE,
                  i18n.sprite()
                ),
            'VAR'
          )
          .appendField(Blockly.Msg.VARIABLES_GET_TAIL);
        this.setOutput(true, Blockly.BlockValueType.SPRITE);
        this.setTooltip(Blockly.Msg.VARIABLES_GET_TOOLTIP);
      },
      getVars: function () {
        return Blockly.Variables.getVars.bind(this)(
          Blockly.BlockValueType.SPRITE
        );
      },
      renameVar: function (oldName, newName) {
        if (Blockly.Names.equals(oldName, this.getFieldValue('VAR'))) {
          this.setFieldValue(newName, 'VAR');
        }
      },
      removeVar: Blockly.Blocks.variables_get.removeVar,
    };
    generator.sprite_variables_get = function () {
      return [
        `'${this.getFieldValue('VAR')}'`,
        Blockly.JavaScript.ORDER_ATOMIC,
      ];
    };
    Blockly.Variables.registerGetter(
      Blockly.BlockValueType.SPRITE,
      'sprite_variables_get'
    );

    Blockly.Blocks.sprite_parameter_get = {
      init() {
        var fieldLabel = new CdoFieldLabel(Blockly.Msg.VARIABLES_GET_ITEM);
        // Must be marked EDITABLE so that cloned blocks share the same var name
        fieldLabel.EDITABLE = true;
        this.setHelpUrl(Blockly.Msg.VARIABLES_GET_HELPURL);
        this.appendDummyInput()
          .appendField(Blockly.Msg.VARIABLES_GET_TITLE)
          .appendField(fieldLabel, 'VAR')
          .appendField(Blockly.Msg.VARIABLES_GET_TAIL);
        this.setOutput(true, Blockly.BlockValueType.SPRITE);
        this.setTooltip(Blockly.Msg.VARIABLES_GET_TOOLTIP);
      },
      renameVar(oldName, newName) {
        if (behaviorEditor.isOpen()) {
          behaviorEditor.renameParameter(oldName, newName);
          behaviorEditor.refreshParamsEverywhere();
        }
      },
      removeVar: Blockly.Blocks.variables_get.removeVar,
    };
    generator.forBlock.sprite_parameter_get = generator.forBlock.variables_get;

    Blockly.Blocks.gamelab_behavior_get = {
      init() {
        var fieldLabel = new CdoFieldLabel(Blockly.Msg.VARIABLES_GET_ITEM);
        // Must be marked EDITABLE so that cloned blocks share the same var name
        fieldLabel.EDITABLE = true;
        this.setHelpUrl(Blockly.Msg.VARIABLES_GET_HELPURL);
        this.setStyle(BlockStyles.BEHAVIOR);
        const mainTitle = this.appendDummyInput()
          .appendField(fieldLabel, 'VAR')
          .appendField(Blockly.Msg.VARIABLES_GET_TAIL);

        if (Blockly.useModalFunctionEditor) {
          var editLabel = new Blockly.FieldIcon(Blockly.Msg.FUNCTION_EDIT);
          Blockly.browserEvents.bind(
            editLabel.fieldGroup_,
            'mousedown',
            this,
            this.openEditor
          );
          mainTitle.appendField(editLabel);
        }

        this.setOutput(true, Blockly.BlockValueType.BEHAVIOR);
        this.setTooltip(Blockly.Msg.VARIABLES_GET_TOOLTIP);
        this.currentParameterNames_ = [];
      },

      openEditor(e) {
        e.stopPropagation();
        behaviorEditor.openEditorForFunction(this, this.getFieldValue('VAR'));
      },

      getVars() {
        return Blockly.Variables.getVars.bind(this)(
          Blockly.BlockValueType.BEHAVIOR
        );
      },

      renameVar(oldName, newName) {
        if (Blockly.Names.equals(oldName, this.getFieldValue('VAR'))) {
          this.setFieldValue(newName, 'VAR');
        }
      },

      renameProcedure(oldName, newName) {
        if (Blockly.Names.equals(oldName, this.getFieldValue('VAR'))) {
          this.setFieldValue(newName, 'VAR');
        }
      },

      getCallName() {
        return this.getFieldValue('VAR');
      },

      setProcedureParameters(paramNames, paramIds, typeNames) {
        Blockly.Blocks.procedures_callnoreturn.setProcedureParameters.call(
          this,
          paramNames.slice(1),
          paramIds && paramIds.slice(1),
          typeNames && typeNames.slice(1)
        );
      },

      mutationToDom() {
        const container = document.createElement('mutation');
        for (let x = 0; x < this.currentParameterNames_.length; x++) {
          const parameter = document.createElement('arg');
          parameter.setAttribute('name', this.currentParameterNames_[x]);
          if (this.currentParameterTypes_[x]) {
            parameter.setAttribute('type', this.currentParameterTypes_[x]);
          }
          container.appendChild(parameter);
        }
        return container;
      },

      domToMutation(xmlElement) {
        this.currentParameterNames_ = [];
        this.currentParameterTypes_ = [];
        for (let childNode of xmlElement.childNodes) {
          if (childNode.nodeName.toLowerCase() === 'arg') {
            this.currentParameterNames_.push(childNode.getAttribute('name'));
            this.currentParameterTypes_.push(childNode.getAttribute('type'));
          }
        }
        // Use parameter names as dummy IDs during initialization. Add dummy
        // "this_sprite" param.
        this.setProcedureParameters(
          [null].concat(this.currentParameterNames_),
          [null].concat(this.currentParameterNames_),
          [null].concat(this.currentParameterTypes_)
        );
      },
    };

    generator.gamelab_behavior_get = function () {
      const name = Blockly.JavaScript.variableDB_.getName(
        this.getFieldValue('VAR'),
        Blockly.Procedures.NAME_TYPE
      );
      const extraArgs = [];
      for (let x = 0; x < this.currentParameterNames_.length; x++) {
        extraArgs[x] =
          Blockly.JavaScript.valueToCode(
            this,
            'ARG' + x,
            Blockly.JavaScript.ORDER_COMMA
          ) || 'null';
      }
      return [
        `new Behavior(${name}, [${extraArgs.join(', ')}])`,
        Blockly.JavaScript.ORDER_ATOMIC,
      ];
    };

    generator.behavior_definition = generator.procedures_defnoreturn;

    Blockly.Procedures.DEFINITION_BLOCK_TYPES.push('behavior_definition');
    Blockly.Variables.registerGetter(
      Blockly.BlockValueType.BEHAVIOR,
      'gamelab_behavior_get'
    );
    delete blockly.Blocks.procedures_defreturn;
    delete blockly.Blocks.procedures_ifreturn;
  },
};
